import React, { useCallback, useEffect, useState } from 'react';
import { BadgeCheck, Loader2, RefreshCw } from 'lucide-react';
import { adminApi } from '../services/api.jsx';
import { annoncerErreur, annoncerSucces, EtatErreur, SqueletteCarte } from '../etats';

/* ═══════════════════════════════════════════════════════════════════════
   LA FORMULE D'UN PROJET, ET CE QU'IL EN A CONSOMMÉ

   La formule est la base du contrat : ses avantages sont recopiés sur le
   projet quand on la fixe, et la plateforme les fait respecter (une séance
   de trop est refusée au coach, un participant de trop au dirigeant).

   Deux cas l'amènent ici :
     - un projet ouvert hors plateforme : l'organisation n'a rien choisi en
       ligne, c'est l'administration qui fixe la formule convenue ;
     - un catalogue modifié : choisir à nouveau la même formule recopie ses
       avantages actuels.
   ═══════════════════════════════════════════════════════════════════════ */

const formaterPrix = (n) => `${new Intl.NumberFormat('fr-FR').format(n || 0).replace(/\s/g, ' ')} FCFA`;

export default function FormuleDuProjet({ projetId, peutModifier }) {
  const [etat, setEtat] = useState(null);
  const [choix, setChoix] = useState('');
  const [enCours, setEnCours] = useState(false);
  // L'objet erreur du chargement : son statut choisit l'écran.
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const d = await adminApi.getFormuleProjet(projetId);
      setEtat(d);
      setChoix(d?.formule || '');
    } catch (e) {
      setErreur(e);
    }
  }, [projetId]);

  useEffect(() => { charger(); }, [charger]);

  const fixer = async () => {
    if (!choix) return;
    setEnCours(true);
    try {
      const d = await adminApi.fixerFormuleProjet(projetId, choix);
      setEtat((e) => ({ ...e, ...d }));
      annoncerSucces(choix === etat?.formule
        ? 'Les avantages actuels de la formule ont été recopiés sur le projet.'
        : 'Formule fixée. L’organisation en est informée.');
    } catch (e) {
      annoncerErreur(e?.message || 'La formule n’a pas pu être fixée.');
    } finally {
      setEnCours(false);
    }
  };

  if (!etat) {
    return erreur ? (
      <section className="bg-[var(--bg-card)] rounded-carte shadow-carte">
        <EtatErreur erreur={erreur} onReessayer={charger} compact />
      </section>
    ) : <SqueletteCarte />;
  }

  const detail = etat?.formule_detail;
  const avantages = etat?.avantages || [];
  const possibles = etat?.formules_possibles || [];

  return (
    <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-pastille bg-[var(--bg-main)] grid place-items-center">
            <BadgeCheck className="w-5 h-5 text-[var(--accent-texte)]" />
          </span>
          <div>
            <h2 className="titre-carte">Formule et avantages</h2>
            <p className="text-[13px] text-[var(--text-muted)]">
              {detail
                ? <>{detail.nom} · <span className="font-semibold text-[var(--text-bold)]">{formaterPrix(detail.prix_fcfa)}</span> · {detail.duree}</>
                : 'Aucune formule fixée pour ce projet.'}
            </p>
          </div>
        </div>
      </div>

      {etat && !etat.configure && (
        <p className="encart encart-attention mb-4">
          {detail
            ? 'Cette formule n’a pas encore d’avantages renseignés : la plateforme n’applique aucune limite à ce projet.'
            : 'Sans formule, la plateforme n’applique aucune limite à ce projet. Fixez celle qui a été convenue avec l’organisation.'}
        </p>
      )}

      {avantages.length > 0 && (
        <ul className="grid sm:grid-cols-2 gap-3 mb-5">
          {avantages.map((a) => {
            // Un avantage sans compteur (rapport, certificat, kit…) : il est
            // au contrat, la plateforme ne le décompte pas.
            if (a.utilise == null) {
              return (
                <li key={a.code} className="rounded-pastille border border-[var(--border)] p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-[13px] font-semibold text-[var(--text-bold)]">{a.libelle}</p>
                    <p className="text-[12px] font-semibold text-[var(--accent-texte)]">
                      {a.type === 'inclus' ? 'Compris' : `× ${a.quantite}`}
                    </p>
                  </div>
                  <p className="text-[11.5px] text-[var(--text-muted)] mt-1">{a.phrase}</p>
                </li>
              );
            }
            const pct = a.quantite ? Math.min(100, Math.round((a.utilise / a.quantite) * 100)) : 0;
            const plein = a.utilise >= a.quantite;
            return (
              <li key={a.code} className="rounded-pastille border border-[var(--border)] p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-[13px] font-semibold text-[var(--text-bold)]">{a.libelle}</p>
                  <p className={`text-[13px] font-semibold tabular-nums ${plein ? 'text-[var(--attention)]' : 'text-[var(--text-bold)]'}`}>
                    {a.utilise} / {a.quantite}
                  </p>
                </div>
                <div className="h-1.5 rounded-controle bg-[var(--bg-main)] mt-2 overflow-hidden">
                  <div className="h-full rounded-controle"
                       style={{ width: `${pct}%`, background: plein ? 'var(--attention)' : 'var(--accent)' }} />
                </div>
                <p className="text-[11.5px] text-[var(--text-muted)] mt-1">
                  {a.periode ? `En ${a.periode}. ` : ''}{plein ? 'Limite atteinte.' : `${a.restant} restant${a.restant > 1 ? 's' : ''}.`}
                </p>
              </li>
            );
          })}
        </ul>
      )}

      {peutModifier && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-[1_1_260px]">
            <span className="block surtitre text-[var(--text-muted)] mb-1.5">
              {etat?.formule ? 'Changer ou réappliquer la formule' : 'Fixer la formule convenue'}
            </span>
            <select value={choix} onChange={(e) => setChoix(e.target.value)} className="champ">
              <option value="">— Choisir —</option>
              {possibles.map((f) => (
                <option key={f.code} value={f.code}
                        disabled={etat?.echeancier_etabli && f.code !== etat?.formule}>
                  {f.nom} · {formaterPrix(f.prix_fcfa)}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={fixer} disabled={!choix || enCours} aria-busy={enCours || undefined} className="act">
            {enCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {choix && choix === etat?.formule ? 'Réappliquer' : 'Fixer la formule'}
          </button>
          {etat?.echeancier_etabli && (
            <p className="w-full text-[11.5px] text-[var(--text-muted)]">
              L’échéancier de paiement est établi : la formule ne peut plus changer, seulement être réappliquée.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
